import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider, useLang } from "@/context/LangContext";
import Samples from "@/pages/Samples";
import Teacher from "@/pages/Teacher";
import AdminClasses from "@/pages/AdminClasses";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import AdminVoices from "@/pages/AdminVoices";
import ResetPassword from "@/pages/ResetPassword";
import Library from "@/pages/Library";
import Admin from "@/pages/Admin";
import AdminBooks from "@/pages/AdminBooks";
import Certificate from "@/pages/Certificate";
import { BadgeCelebration } from "@/components/badges/BadgeCelebration";
import { OfflineSync } from "@/components/OfflineSync";

const SmoothScroll = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const RoleRoute = ({ role, login, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-stone-600">...</div>;
  if (!user) return <Navigate to={login} replace />;
  if (user.role !== role) return <Navigate to="/library" replace />;
  return children;
};

const AdminRoute = ({ children }) => <RoleRoute role="admin" login="/admin/login">{children}</RoleRoute>;

const Shell = ({ children }) => {
  const { dir } = useLang();
  return <div className="App grain" dir={dir}>{children}</div>;
};

function App() {
  return (
    <LangProvider>
    <Shell>
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/library" element={<Library />} />
            <Route path="/samples" element={<Samples />} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/admin/books" element={<AdminRoute><AdminBooks /></AdminRoute>} />
            <Route path="/admin/classes" element={<AdminRoute><AdminClasses /></AdminRoute>} />
            <Route path="/admin/voices" element={<AdminRoute><AdminVoices /></AdminRoute>} />
            <Route path="/teacher/login" element={<Login mode="teacher" />} />
            <Route path="/admin/login" element={<Login mode="admin" />} />
            <Route path="/teacher" element={<RoleRoute role="teacher" login="/login"><Teacher /></RoleRoute>} />
            <Route path="/certificate" element={<RoleRoute role="user" login="/login"><Certificate /></RoleRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <BadgeCelebration />
        <OfflineSync />
        <Toaster position="top-center" richColors toastOptions={{ style: { fontFamily: "Cairo, sans-serif" } }} />
      </AuthProvider>
    </Shell>
    </LangProvider>
  );
}

export default App;
